import { useCallback, useEffect, useState } from 'react';

import { errorKey, type AuthApi, type Session } from '../auth/api.ts';
import { useI18n } from '../i18n/I18nProvider';
import { avatarUrl, type AccountProfile, type GameApi, type GameProfile }
  from '../profile/api.ts';
import { AvatarPicker } from '../profile/AvatarPicker';
import { HistorySheet } from '../profile/HistorySheet';
import { MailSheet } from '../profile/MailSheet';
import { NicknameSheet } from '../profile/NicknameSheet';
import { PurchaseSheet } from '../shop/PurchaseSheet';
import { VipSheet } from '../profile/VipSheet';
import { WithdrawSheet } from '../profile/WithdrawSheet';
import { PresetAvatar } from '../profile/PresetAvatar';
import { Button } from '../ui/Button';
import { copyText } from '../ui/clipboard';
import { Icon } from '../ui/Icon';
import '../profile/profile.css';

export function ProfileTab(
  { api, game, session, accountUrl, onNeedLogin, onNickname }:
  {
    api: AuthApi;
    game: GameApi;
    session: Session | null;
    accountUrl: string;
    onNeedLogin(): void;
    /** 昵称拉到了 / 改了 —— 顶栏右上角跟着变 */
    onNickname?(nickname: string): void;
  },
) {
  const { t, i18n } = useI18n();
  const [acc, setAcc] = useState<AccountProfile | null>(null);
  const [gp, setGp] = useState<GameProfile | null>(null);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [vipOpen, setVipOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [buysOpen, setBuysOpen] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  //: 复制 ID 的结果：ok 显示「已复制」，fail 提示手动复制，null 什么都不显示
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  // 未读数。**跟着个人页一起来的**（`AccountProfile.mailUnread`）——
  // 单开一个请求的话，进个人页会多一次往返，而它要显示的只是一个数字。
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!session) return;
    // **两边各拉各的。** 哪边挂了就少那一块，而不是整页空白 ——
    // 所以用 allSettled 而不是 all
    const [a, g] = await Promise.allSettled([
      api.profile(session.token), game.profile(session.token)]);
    if (a.status === 'fulfilled') {
      setAcc(a.value);
      setUnread(a.value.mailUnread ?? 0);
      if (a.value.nickname) onNickname?.(a.value.nickname);
    }
    if (g.status === 'fulfilled') setGp(g.value);
    const bad = [a, g].find((r) => r.status === 'rejected');
    setErr(bad ? t(errorKey((bad as PromiseRejectedResult).reason)) : '');
  }, [api, game, session, onNickname, t]);

  useEffect(() => { void load(); }, [load]);

  if (!session) {
    return (
      <section className="stack">
        <h1 className="h1">{t('tab.profile')}</h1>
        <div className="placeholder">
          <p className="small placeholder__note">{t('settings.notLoggedIn')}</p>
          <Button variant="primary" onClick={onNeedLogin}>
            {t('action.loginOrRegister')}
          </Button>
        </div>
      </section>
    );
  }

  async function copyId() {
    if (!session) return;
    // **复制失败也要说一声** —— webview、隐私模式常常不给写剪贴板，
    // 点了没反应的话，玩家会以为已经复制上了，去客服那边粘出一片空白
    const ok = await copyText(session.uid);
    setCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setCopied(null), ok ? 1800 : 4000);
  }

  const uploaded = avatarUrl(acc?.avatar ?? null, accountUrl);
  const renamesLeft = acc?.renamesLeft ?? null;
  const vip = gp?.vip;

  // 金额一律走它：币种/符号/小数位都是服务端下发的，四个地方各写一遍
  // 迟早会漏掉其中一个。gp 还没到时不会被调用（每处都在 gp 的分支里）。
  const money = (n: number) =>
    i18n.money(n, gp!.cur, { symbol: gp!.sym, decimals: gp!.dec });

  const histLabel = gp
    ? `${t('history.title')} · ${t('profile.rounds')} ${i18n.num(gp.rounds)}`
    : t('history.title');
  const mailLabel = unread > 0 ? `${t('mail.entry')} (${i18n.num(unread)})` : t('mail.entry');

  return (
    <section className="stack">
      <div className="spread prof__title">
        <h1 className="h1">{t('tab.profile')}</h1>
        {/* 投注历史、站内信、购买记录：三个"去翻记录"的入口收成标题右边的图标。
            做成三张卡的话它们占掉小半屏，每张却只有一个"查看"。
            图标没有字，**名字放进 aria-label 和 title** —— 读屏器念得出、鼠标悬停看得到 */}
        <div className="row prof__tools">
          <Button variant="icon" onClick={() => setHistOpen(true)}
                  aria-label={histLabel} title={histLabel}>
            <Icon name="history" />
          </Button>
          <Button variant="icon" className="prof__tool" onClick={() => setMailOpen(true)}
                  aria-label={mailLabel} title={mailLabel}>
            <Icon name="mail" />
            {/* 未读红点压在图标角上。数字已经在 aria-label 里了，这里不再念一遍 */}
            {unread > 0 && (
              <span className="mail__badge num" aria-hidden="true">
                {unread > 99 ? '99+' : i18n.num(unread)}
              </span>
            )}
          </Button>
          <Button variant="icon" onClick={() => setBuysOpen(true)}
                  aria-label={t('shop.history')} title={t('shop.history')}>
            <Icon name="receipt" />
          </Button>
        </div>
      </div>
      {err && <p className="field__err" role="alert">{err}</p>}

      {/* ---- 头 ---- */}
      <div className="card row prof__head">
        <button type="button" className="prof__avatar" onClick={() => setPicking(true)}
                aria-label={t('profile.changeAvatar')}>
          {uploaded
            ? <img src={uploaded} alt="" width={56} height={56} />
            : <PresetAvatar id={acc?.avatar ?? 'preset:01'} />}
        </button>
        <div className="stack prof__id">
          {/* 名字本身就是入口。用 <button> 不是给 <span> 挂 onClick：
              键盘要能 Tab 到，读屏器要念得出"这是个能改的东西"。
              小铅笔是给第一次来的人的提示 —— 光一个名字看不出能点。 */}
          <button type="button" className="prof__name"
                  onClick={() => setNaming(true)} aria-label={t('profile.editNickname')}>
            <span className="h2 prof__nick">{acc?.nickname ?? t('profile.noNickname')}</span>
            <Icon name="edit" className="prof__edit" />
          </button>
          {/* 剩几次写在名字底下 —— 点之前就该知道改名是有限的 */}
          {renamesLeft !== null && (
            <span className="small dim">
              {renamesLeft > 0 ? t('profile.renamesLeft', { n: renamesLeft }) : t('profile.renamesNone')}
            </span>
          )}
          {/* ID 和复制键**分开**，不再塞进名字那个按钮里：按钮里不能再套按钮，
              而且"点名字改名"和"点复制"是两件事，点错一次就会弹出改名框 */}
          <div className="row prof__uid">
            <span className="small dim num">{t('profile.idLabel', { v: session.uid })}</span>
            <button type="button" className="prof__copy" onClick={() => void copyId()}
                    aria-label={t('profile.copyId')}>
              <Icon name={copied === 'ok' ? 'check' : 'copy'} />
            </button>
            {copied === 'ok' && (
              <span className="small prof__copied" role="status">{t('profile.idCopied')}</span>
            )}
          </div>
          {copied === 'fail' && (
            <span className="small field__err" role="alert">{t('profile.copyFailed')}</span>
          )}
        </div>
        {/* 徽章本身就是入口 —— 玩家看到"VIP 3"第一反应就是想点它。
            用 <button> 不是给 <span> 加 onClick：键盘和读屏器要能用。 */}
        {vip && (
          <button type="button" className="vip__badge vip__badge--btn"
                  onClick={() => setVipOpen(true)} aria-label={t('vip.open')}>
            VIP {vip.level}
          </button>
        )}
      </div>

      {/* ---- 钱：余额 + 打码量 ----
          打码量就是"这些钱能不能提"的条件。**和余额、提现按钮放在一张卡里**
          才读得出这层关系 —— 分成两张卡的话，玩家点了提现被拦，才回头去找那个进度条。 */}
      <div className="card stack">
        <div className="spread">
          <span className="small dim">{t('profile.balance')}</span>
          <span className="h2 num">
            {gp ? money(gp.bal) : '—'}
          </span>
        </div>
        {gp && (
          <div className="stack prof__wager">
            <div className="spread">
              <span className="small dim">{t('profile.wager')}</span>
              {gp.wager.done && gp.wager.required && (
                <span className="badge">{t('profile.wagerDone')}</span>)}
            </div>
            {gp.wager.required ? (
              <>
                <Bar ratio={gp.wager.target ? gp.wager.turnover / gp.wager.target : 1} />
                <span className="small dim num">
                  {money(gp.wager.turnover)} / {money(gp.wager.target)}
                </span>
              </>
            ) : (
              // target 是 0 表示**没有打码要求**，不是"进度 0%"
              <span className="small dim">{t('profile.wagerNone')}</span>
            )}
          </div>
        )}
        {/* 余额没拉到时不给按钮：点开是一个空面板，那比没有按钮更让人困惑 */}
        <Button variant="ghost" className="btn--wide" disabled={!gp}
                onClick={() => setWithdrawing(true)}>
          {t('withdraw.action')}
        </Button>
      </div>

      {/* ---- VIP ---- */}
      {vip && (
        <div className="card stack">
          <div className="spread">
            <span className="h2">{t('profile.vip')}</span>
            <span className="small dim">
              {t('profile.deposited', { v: money(vip.depositTotal) })}
            </span>
          </div>
          <Bar ratio={vip.ratio} />
          {/* 徽章在头像那一行，小而且不一定被注意到。这里再给一个明说的入口。 */}
          <button type="button" className="linkish vip__more"
                  onClick={() => setVipOpen(true)}>
            {t('vip.open')}
          </button>
          <span className="small dim">
            {vip.nextAt === null
              ? t('profile.maxLevel')
              : t('profile.toNextLevel', {
                  n: vip.nextLevel!,
                  v: money(vip.nextAt - vip.depositTotal),
                })}
          </span>
          <div className="prof__perks">
            <Perk label={t('profile.perkFreeSpins')} value={String(vip.dailyFreeSpins)} />
            <Perk label={t('profile.perkDiscount')}
                  value={`+${(vip.depositDiscount * 100).toFixed(0)}%`} />
            <Perk label={t('profile.freeSpinsLeft')} value={String(vip.freeSpins)} />
          </div>
          {/*
            **没有可领的就置灰**，而且按钮上要说清楚是哪一种"没有"：

              今天已领取        明天再来
              当前等级没奖励    去充值升级（0 级的每日免费局是 0）

            两种都画成一个灰按钮、写一样的字的话，0 级玩家会以为是坏了。

            `canClaim` 是**服务端算的** —— 前端按 `dailyFreeSpins > 0` 自己判的话，
            以后加第二种可领的奖励，按钮会一直是灰的。
          */}
          <Button variant="primary" className="btn--wide"
                  disabled={!vip.canClaim || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await game.claimVip(session.token);
                      await load();
                    } catch (e) { setErr(t(errorKey(e))); } finally { setBusy(false); }
                  }}>
            {vip.claimedToday ? t('profile.claimed')
              : !vip.hasReward ? t('profile.noRewardYet')
              : t('profile.claimDaily')}
          </Button>
          {/* 0 级的人要知道**怎么才会有** —— 只给一个灰按钮等于让他猜 */}
          {!vip.hasReward && vip.nextLevel !== null && (
            <span className="small dim">
              {t('profile.rewardFromLevel', { n: vip.nextLevel })}
            </span>
          )}
        </div>
      )}

      <HistorySheet
        open={histOpen}
        api={api}
        token={session.token}
        money={(n) => (gp ? money(n) : String(n))}
        onClose={() => setHistOpen(false)}
      />

      <AvatarPicker
        open={picking}
        api={api}
        session={session}
        presets={acc?.presets ?? []}
        current={acc?.avatar ?? null}
        accountUrl={accountUrl}
        onClose={() => setPicking(false)}
        onDone={() => { setPicking(false); void load(); }}
      />

      {/* 提现成功会扣钱 —— onDone 让个人页把余额重拉一次，
          不然这个页面上那个数还是旧的（而玩家会拿它去下注）。 */}
      {/* 改完直接把新名字塞进本地状态，不重拉整个 profile ——
          重拉会把余额、VIP、打码量全抖一遍，而只有一个字段变了。 */}
      <NicknameSheet
        open={naming}
        api={api}
        token={session.token}
        current={acc?.nickname ?? null}
        renamesLeft={renamesLeft}
        onDone={(r) => {
          setAcc((a) => (a ? { ...a, nickname: r.nickname, renamesLeft: r.renamesLeft } : a));
          onNickname?.(r.nickname);
        }}
        onClose={() => setNaming(false)}
      />

      <MailSheet
        open={mailOpen}
        api={api}
        token={session.token}
        game={game}
        money={(n) => (gp ? money(n) : String(n))}
        onUnread={setUnread}
        // 领了附件，余额和打码进度都变了 —— 重拉一次
        onClaimed={() => { void load(); }}
        onClose={() => setMailOpen(false)}
      />

      <PurchaseSheet
        open={buysOpen}
        api={api}
        token={session.token}
        money={(n) => (gp ? money(n) : String(n))}
        onClose={() => setBuysOpen(false)}
      />

      <VipSheet
        open={vipOpen}
        game={game}
        token={session.token}
        vip={vip ?? null}
        money={(n) => (gp ? money(n) : String(n))}
        onClose={() => setVipOpen(false)}
      />

      <WithdrawSheet
        open={withdrawing}
        api={api}
        game={game}
        token={session.token}
        money={(n) => (gp ? money(n) : String(n))}
        onDone={() => { void load(); }}
        onClose={() => setWithdrawing(false)}
      />

    </section>
  );
}

function Bar({ ratio }: { ratio: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
  return (
    <div className="bar" role="progressbar" aria-valuenow={pct}
         aria-valuemin={0} aria-valuemax={100}>
      <span className="bar__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function Perk({ label, value }: { label: string; value: string }) {
  return (
    <div className="perk">
      <span className="perk__v num">{value}</span>
      <span className="perk__l">{label}</span>
    </div>
  );
}

